import { useEffect } from 'react'
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom'

// Content, Templates and Asset library nest a node's page under its folders
// (/org/node-content/node/963/node/970/course/964), so the trail names each
// folder. Each area also takes a node at any depth by its id alone
// (/org/node-content/course/964), which is what the audit log's links, a
// course launch's and old bookmarks use -- and those read "Content > AR
// Demo", with no folder (audit F3). Once the area's list has loaded, such an
// address is replaced by the node's place under its folders; what follows
// the node (a course's edit pages, a file's versions) and the query stay, so
// every existing link keeps working.

const NODE = /^\/(course|template|library|node)\/(\d+)(\/.*)?$/

// The nested address for `pathname`, or null when it is already in place,
// the node is at the top, or the node or one of its folders isn't in `nodes`.
export const nestedNodePath = (base, pathname, nodes) => {
  if (!pathname || !pathname.startsWith(`${base}/`)) return null
  const match = pathname.slice(base.length).match(NODE)
  if (!match) return null
  const [, kind, idText, rest = ''] = match
  const byID = new Map((nodes || []).map(node => [node.id, node]))
  const node = byID.get(Number(idText))
  if (!node || !node.parentID) return null

  const folders = []
  const seen = new Set([node.id])
  let parentID = node.parentID
  while (parentID) {
    const parent = byID.get(parentID)
    if (!parent || seen.has(parent.id)) return null
    seen.add(parent.id)
    folders.unshift(parent.id)
    parentID = parent.parentID
  }

  return `${base}${folders.map(id => `/node/${id}`).join('')}/${kind}/${node.id}${rest}`
}

// Moves a flat address to its place once `loaded`; `nodes` is the area's
// whole list.
export const useNestedNodePath = (base, nodes, loaded) => {
  const history = useHistory()
  const location = useLocation()
  const nested = loaded ? nestedNodePath(base, location.pathname, nodes) : null

  useEffect(
    () => {
      if (nested) history.replace({ pathname: nested, search: location.search, hash: location.hash, state: location.state })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [nested]
  )
}

// Drawn once at the top of an area, inside its DataProvider: `useData` is the
// area's data hook ({ items, loaded }); the area's address is the route's.
export const NestedNodeAddress = ({ useData }) => {
  const data = useData()
  const { url } = useRouteMatch()
  useNestedNodePath(url, data.items, data.loaded)
  return null
}
